import { Field, Formik, FormikProps, FormikValues } from 'formik';
import React, { FC, useState, useEffect, useRef, RefObject } from 'react';
import { Badge, Button, Card, Spinner, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { DataFormModel } from '../../../components/form/public/utilities/FormModels';
import useSubmission from '../../../hooks/api/submission/useSubmission';
import { updateSubmission } from '../../../services/form/FormService';
import { RootState } from '../../../store';
import SubmissionSteps, { SubmissionStepsRef } from './components/SubmissionSteps';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatValues } from '../../../components/form/public/utilities/FormUtilities';
import { addErrorToast, addSuccessToast } from '../../../components/errors/toast/ToastUtils';
import { StepModel } from '../../../models/form/step';
import ModalComponent from '../../../components/modal/ModalComponent';
import { useHistory } from 'react-router';
import useSubmissionList from '../../../hooks/api/submission/useSubmissionList';
import classNames from 'classnames';
import LoadingSpinner from '../../../components/layout/loadingSpinner/LoadingSpinner';
import { getSecuritySection, SecuritySections, SecurityModel } from '../../../models/security/security';
import { cancelPaymentIntentById, refundPaymentById } from '../../../services/payment/PaymentService';
import * as Yup from 'yup';
import FieldWrapper from '../../../components/library/inputs/fieldWrapper/FieldWrapper';
import CheckboxInput from '../../../components/library/inputs/checkbox/CheckboxInput';
import CurrencyInput from '../../../components/library/inputs/currency/CurrencyInput';
import moment from 'moment';
import { FallbackPage } from '../../../components/layout/navbar/Navbar';
import useDeleteSubmission from '../../../hooks/api/submission/useDeleteSubmission';

interface Values {
  amount: string;
}
const SubmissionPage: FC = () => {
  const [editMode, setEditMode] = useState(false);
  const [stepsStateInfo, setStepsStateInfo] = useState<StepModel[] | undefined>(undefined);
  const [showModalDelete, setShowModalDelete] = useState<boolean>(false);
  const [showModalRefund, setShowModalRefund] = useState<boolean>(false);
  const [refundStripeFees, setRefundStripeFees] = useState<boolean>(true);
  const [partialRefund, setPartialRefund] = useState<boolean>(false);
  const [stripeFeesAmount] = useState<string>('0.00');
  const [totalAmount, setTotalAmount] = useState<string>('0.00');
  const [formSubmissionSecurity, setFormSubmissionSecurity] = useState<SecurityModel | null>(null);
  const { currentSubmission, setCurrentSubmissionId, setCurrentFormId, isLoading, currentForm, reloadSubmission } =
    useSubmission();
  const { reloadSubmissions } = useSubmissionList();
  const { formId, submissionId } = useParams<any>();
  const organizationId = useSelector((state: RootState) => state.organization.organization?.id);
  const security = useSelector((state: RootState) => state.organization.organization?.security);
  const intl = useIntl();
  const history = useHistory();
  const formikRef = useRef<FormikValues>();
  const refundRef = useRef<FormikProps<Values>>(null);
  const stepsRef = useRef() as RefObject<SubmissionStepsRef>;
  const topbar = document.querySelector('header.topbar');
  const { deleteSubmission, isLoading: isDeleteLoading } = useDeleteSubmission();

  useEffect(() => {
    if (formId && submissionId && organizationId) {
      setCurrentFormId(formId);
      setCurrentSubmissionId(submissionId);
    }
  }, [organizationId]);

  useEffect(() => {
    if (currentSubmission && currentSubmission?.payment) {
      setTotalAmount((currentSubmission.payment.total / 100).toFixed(2));
    }
  }, [currentSubmission, currentForm]);

  useEffect(() => {
    if (security) {
      setFormSubmissionSecurity(getSecuritySection(security, SecuritySections.FORM_SUBMISSION));
    }
    if (formSubmissionSecurity && !formSubmissionSecurity?.read) {
      history.push(FallbackPage);
    }
  }, [security, formSubmissionSecurity]);

  useEffect(() => {
    let newAmount = '';
    if (refundStripeFees) {
      newAmount = Number(totalAmount).toFixed(2);
    } else if (!refundStripeFees) {
      newAmount = partialRefund
        ? Number(totalAmount).toFixed(2)
        : (Number(totalAmount) - Number(stripeFeesAmount)).toFixed(2);
    }
    refundRef.current?.setFieldValue('amount', newAmount, true);
  }, [refundStripeFees]);

  const handleStepsCallback = (steps) => {
    setStepsStateInfo(steps);
  };

  const onUpdateSubmission = async () => {
    if (stepsStateInfo && formikRef.current?.values && organizationId && currentForm?.id && currentSubmission?.id) {
      const submissionValues = formatValues(stepsStateInfo, formikRef.current?.values);
      const formData = new FormData();
      const submission: DataFormModel = { ...currentSubmission, fields: submissionValues.fields };
      formData.append('Content', JSON.stringify(submission));

      for (const file of submissionValues.files) {
        formData.append(file.id, file.value);
      }
      await updateSubmission(organizationId, currentForm?.id, formData, currentSubmission.id)
        .then(() => {
          reloadSubmission();
          addSuccessToast({ message: intl.formatMessage({ id: 'formList.submission.updateSuccess' }) });
        })
        .catch((e) => {
          if (e.response.status === 400) {
            addErrorToast({ message: intl.formatMessage({ id: 'formPublic.postSubmission.noRecaptcha' }) });
          } else if (e.response.status === 403) {
            addErrorToast({ message: intl.formatMessage({ id: 'formPublic.postSubmission.invalidRecaptcha' }) });
          } else {
            addErrorToast({ message: intl.formatMessage({ id: 'formList.submission.updateError' }) });
          }
        });
    }
  };

  const handleBadge = (status: string) => {
    switch (status) {
      case 'succeeded':
      case 'SUCCESS':
        return (
          <Badge bg="success" style={{ color: '#16164C', backgroundColor: '#DAF4EB' }}>
            {intl.formatMessage({ id: 'formPublic.badges.succeeded' })}
          </Badge>
        );
      case 'requires_confirmation':
        return (
          <Badge bg="warning" style={{ color: '#16164C', backgroundColor: '#FCF1DF' }}>
            {intl.formatMessage({ id: 'formPublic.badges.requiresConfirmation' })}
          </Badge>
        );
      case 'requires_payment_method':
        return (
          <Badge bg="warning" style={{ color: '#16164C', backgroundColor: '#FCF1DF' }}>
            {intl.formatMessage({ id: 'formPublic.badges.requiresPaymentMethod' })}
          </Badge>
        );
      case 'requires_action':
        return (
          <Badge bg="warning" style={{ color: '#16164C', backgroundColor: '#FCF1DF' }}>
            {intl.formatMessage({ id: 'formPublic.badges.requiresAction' })}
          </Badge>
        );
      case 'processing':
      case 'PROCESSING':
        return (
          <Badge bg="warning" style={{ color: '#16164C', backgroundColor: '#FCF1DF' }}>
            {intl.formatMessage({ id: 'formPublic.badges.processing' })}
          </Badge>
        );
      case 'failed':
      case 'ERROR':
        return (
          <Badge bg="danger" style={{ color: '#16164C', backgroundColor: '#f0b8b8' }}>
            {intl.formatMessage({ id: 'formPublic.badges.failed' })}
          </Badge>
        );
      case 'canceled':
      case 'CANCELED':
        return (
          <Badge bg="danger" style={{ color: '#16164C', backgroundColor: '#f0b8b8' }}>
            {intl.formatMessage({ id: 'formPublic.badges.canceled' })}
          </Badge>
        );
      case 'refunded':
      case 'REFUNDED':
        return (
          <Badge bg="danger" style={{ color: '#16164C', backgroundColor: '#f0b8b8' }}>
            {intl.formatMessage({ id: 'formPublic.badges.refunded' })}
          </Badge>
        );
      case 'pending_refund':
        return (
          <Badge bg="danger" style={{ color: '#16164C', backgroundColor: '#f0b8b8' }}>
            {intl.formatMessage({ id: 'formPublic.badges.pendingRefund' })}
          </Badge>
        );
      case 'READY':
        return (
          <Badge bg="warning" style={{ color: '#16164C', backgroundColor: '#FCF1DF' }}>
            {intl.formatMessage({ id: 'formPublic.badges.ready' })}
          </Badge>
        );
    }
  };

  const handleCancel = () => {
    stepsRef.current?.cancelEdit();
    setEditMode(false);
  };

  const handleSubmissionDelete = async () => {
    if (!isDeleteLoading && organizationId && currentForm?.id && currentSubmission?.id) {
      await deleteSubmission(organizationId, currentForm.id, currentSubmission.id)
        .then(() => {
          addSuccessToast({ message: intl.formatMessage({ id: 'formList.submission.deleteSuccess' }) });
          reloadSubmissions();
          history.push(`/registrations/submissions/${currentForm.id}`);
        })
        .catch(() => {
          addErrorToast({ message: intl.formatMessage({ id: 'formList.submission.deleteError' }) });
        });
    }
  };

  const handleRefund = async () => {
    if (organizationId && currentSubmission && currentSubmission.payment) {
      const formData = new FormData();
      formData.append('amount', JSON.stringify(Number(refundRef.current?.values.amount) * 100));
      formData.append('refundStripeFees', JSON.stringify(refundStripeFees));

      await refundPaymentById(
        organizationId,
        currentForm.paymentAccount,
        currentSubmission?.payment?.paymentIntentId,
        formData,
      )
        .then(() => {
          reloadSubmission();
          addSuccessToast({ message: intl.formatMessage({ id: 'formList.submission.refundSuccess' }) });
        })
        .catch((e) => {
          if (e.response.status === 400) {
            addErrorToast({ message: intl.formatMessage({ id: 'formPublic.postSubmission.noRecaptcha' }) });
          } else if (e.response.status === 403) {
            addErrorToast({ message: intl.formatMessage({ id: 'formPublic.postSubmission.invalidRecaptcha' }) });
          } else {
            addErrorToast({ message: intl.formatMessage({ id: 'formList.submission.refundError' }) });
          }
        });
      setShowModalRefund(false);
    }
  };

  const handleCancelPayment = async () => {
    if (organizationId && currentSubmission && currentSubmission.payment) {
      await cancelPaymentIntentById(
        organizationId,
        currentForm.paymentAccount,
        currentSubmission?.payment?.paymentIntentId,
      )
        .then(() => {
          reloadSubmission();
          addSuccessToast({ message: intl.formatMessage({ id: 'formList.submission.cancelSuccess' }) });
        })
        .catch((e) => {
          if (e.response.status === 400) {
            addErrorToast({ message: intl.formatMessage({ id: 'formPublic.postSubmission.noRecaptcha' }) });
          } else if (e.response.status === 403) {
            addErrorToast({ message: intl.formatMessage({ id: 'formPublic.postSubmission.invalidRecaptcha' }) });
          } else {
            addErrorToast({ message: intl.formatMessage({ id: 'formList.submission.cancelError' }) });
          }
        });
    }
    setShowModalRefund(false);
  };

  const paiementInfoTable = (currentSubmission) => {
    return (
      <div className="d-flex flex-column align-items-start justify-content-center pb-3">
        <Table className="refund-table" hover responsive>
          <thead>
            <tr>
              <th className="refund-table-header text-start">
                {intl.formatMessage({ id: 'formList.refundHeaders.paymentIntentId' })}
              </th>
              <th className="refund-table-header">Date</th>
              <th className="refund-table-header">
                {intl.formatMessage({ id: 'formList.refundHeaders.paymentTotal' })}
              </th>
              <th className="refund-table-header">{intl.formatMessage({ id: 'formList.refundHeaders.stripeFees' })}</th>
              <th className="refund-table-header">{intl.formatMessage({ id: 'formList.refundHeaders.status' })}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="text-start">{currentSubmission?.payment?.paymentIntentId}</td>
              <td>{moment(currentSubmission.date).format('DD-MM-YYYY')}</td>
              <td>{totalAmount}$</td>
              <td>{stripeFeesAmount}$</td>
              {/* <td>{handleBadge()}</td> */}
            </tr>
          </tbody>
        </Table>
      </div>
    );
  };

  const refundAmountErrorMessages = Yup.object().shape({
    amount: Yup.string()
      .min(1, intl.formatMessage({ id: 'formSettings.errorMessage.short' }))
      .required(intl.formatMessage({ id: 'formSettings.errorMessage.required' }))
      .test(
        'is-amount-more-than-total',
        intl.formatMessage({ id: 'formList.submission.refundAmountError' }),
        (value) => {
          if (value) {
            if (partialRefund || refundStripeFees) {
              return Number(value) <= Number(totalAmount);
            } else {
              return Number(value) <= Number(totalAmount) - Number(stripeFeesAmount);
            }
          }
          return false;
        },
      ),
  });

  return (
    <div className="d-flex submission-page">
      <Card className="flex-1">
        {isLoading && <LoadingSpinner />}
        {!isLoading && currentSubmission && (
          <Card.Body>
            <div style={{ marginTop: 24 }}>
              {currentForm && (
                <SubmissionSteps
                  form={currentForm}
                  data={currentSubmission as DataFormModel}
                  readOnlyMode={!editMode}
                  formikRef={formikRef}
                  stepsStateProp={handleStepsCallback}
                  ref={stepsRef}
                />
              )}
            </div>
          </Card.Body>
        )}
      </Card>
      <div className="d-none d-lg-block ms-4" style={{ width: 400 }}>
        <Card className={classNames('form-summary', { 'offset-topbar': topbar })}>
          <Card.Header className="form-summary-header">
            <h5 className="mb-0 submission-title"> {intl.formatMessage({ id: 'formList.submission.summary' })}</h5>
          </Card.Header>
          {!isLoading && (
            <Card.Body style={{ fontSize: 14 }}>
              <div className="submission-info">
                <b>{intl.formatMessage({ id: 'formList.submission.formId' })}</b> {formId}
              </div>
              <div className="mt-3 submission-info">
                <b>{intl.formatMessage({ id: 'formList.submission.submissionId' })}</b> {submissionId}
              </div>
              <div className="mt-3 submission-info">
                <b>{intl.formatMessage({ id: 'formList.submission.sentDate' })}</b>
                {currentSubmission ? currentSubmission.date : ''}
              </div>
              {(currentSubmission?.payment || currentSubmission?.coupon) && (
                <div className="mt-3 submission-info">
                  <b>{intl.formatMessage({ id: 'formList.submission.paymentStatus' })}</b>
                  {currentSubmission?.coupon && (
                    <div>
                      {intl.formatMessage({ id: 'formList.submission.discount' })}:
                      {currentSubmission.coupon.discountTotal && (
                        <b>{` -${(currentSubmission.coupon.discountTotal / 100).toFixed(2)} $ `}</b>
                      )}
                      <span>{`(${currentSubmission.coupon.code})`}</span>
                    </div>
                  )}
                  {currentSubmission?.payment && currentSubmission?.payment?.method != 'other' && (
                    <div>
                      {currentSubmission?.payment.modeDefinition &&
                        currentSubmission?.payment.modeDefinition?.discount &&
                        currentSubmission?.payment.modeDefinition?.discount.value > 0 && (
                          <div>
                            <div>
                              {intl.formatMessage({ id: 'formList.submission.discount' })}:
                              <b>
                                {` ${currentSubmission?.payment.modeDefinition?.discount.value} ${
                                  currentSubmission?.payment.modeDefinition?.discount.type === 'percent' ? '%' : '$'
                                }`}
                              </b>
                            </div>
                          </div>
                        )}
                      {currentSubmission?.payment.paymentDetails && (
                        <div>
                          <div>
                            {currentSubmission?.payment.paymentDetails.map((detail, index) => (
                              <div key={index} className="d-flex d-flex-row">
                                <div className="me-3">{moment(detail.date).format('DD-MM-YYYY')}</div>
                                <div className="me-3">
                                  <b>{(detail.total / 100).toFixed(2)}$</b>
                                </div>
                                <div>{handleBadge(detail.status)}</div>
                              </div>
                            ))}
                          </div>
                          <div>
                            {intl.formatMessage({ id: 'formList.submission.total' })}:
                            <b>
                              {(
                                currentSubmission?.payment.paymentDetails.reduce((acc, { total }) => acc + total, 0) /
                                100
                              ).toFixed(2)}
                              $
                            </b>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {currentSubmission?.payment && currentSubmission?.payment?.method === 'other' && (
                    <div>
                      <div>{intl.formatMessage({ id: 'formList.submission.otherMethod' })}</div>
                      <div>
                        {intl.formatMessage({ id: 'formList.submission.total' })}:
                        <b>{(currentSubmission?.payment.total / 100).toFixed(2)}$</b>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="submission-button-wrapper">
                {!editMode && (
                  <Button onClick={() => setEditMode(true)} disabled={!formSubmissionSecurity?.update}>
                    <span className="mdi mdi-pencil" /> {intl.formatMessage({ id: 'formList.submission.edit' })}
                  </Button>
                )}
                {editMode && (
                  <Button
                    onClick={() => {
                      setEditMode(false);
                      onUpdateSubmission();
                    }}
                    disabled={!formSubmissionSecurity?.update}
                  >
                    <span className="mdi mdi-content-save" />
                    {isLoading && <Spinner animation="border" size="sm"></Spinner>}
                    {!isLoading && intl.formatMessage({ id: 'formList.submission.save' })}
                  </Button>
                )}
                {editMode && formikRef && (
                  <Button className="m-1" variant="outline-primary" onClick={() => handleCancel()}>
                    <span className="mdi mdi-redo-variant" />
                    {intl.formatMessage({ id: 'formList.submission.cancel' })}
                  </Button>
                )}
                <Button onClick={() => setShowModalDelete(true)} variant="danger">
                  <span className="mdi mdi-trash-can" /> {intl.formatMessage({ id: 'delete' })}
                </Button>
                {/* <Button
                  variant="primary"
                  onClick={() => setShowModalRefund(true)}
                  disabled={!formSubmissionSecurity?.update || !currentForm.paymentEnabled}
                >
                  <div className="d-inline-flex">
                    <span className="mdi mdi mdi-undo"></span>
                    {currentForm && currentSubmission?.payment?.status == 'succeeded'
                      ? intl.formatMessage({ id: 'formList.submission.refund' })
                      : intl.formatMessage({ id: 'formList.submission.cancelPayment' })}
                  </div>
                </Button>
                <Button
                  variant="primary"
                  className="delete-btn-header"
                  onClick={() => {
                    setShowModalDelete(true);
                  }}
                  disabled={!formSubmissionSecurity?.delete}
                >
                  <span className="mdi mdi-trash-can-outline"></span>
                </Button> */}
              </div>
            </Card.Body>
          )}
        </Card>
      </div>
      <ModalComponent
        closeSettings={() => setShowModalDelete(false)}
        buttons={[
          {
            text: intl.formatMessage({ id: 'delete' }),
            buttonVariant: 'primary',
            loading: isDeleteLoading,
            onClick: () => handleSubmissionDelete(),
          },
        ]}
        modalHeading=""
        show={showModalDelete}
      >
        <FormattedMessage id="formList.submission.modalMessage"></FormattedMessage>
      </ModalComponent>
      <ModalComponent
        closeSettings={() => setShowModalRefund(false)}
        buttons={[
          {
            text: intl.formatMessage({ id: 'formList.submission.confirm' }),
            buttonVariant: 'primary',
            onClick: () => {
              if (currentSubmission?.payment?.status == 'succeeded') {
                if (refundRef.current?.isValid) {
                  refundRef.current?.handleSubmit();
                } else {
                  addErrorToast({ message: intl.formatMessage({ id: 'formPublic.errorRefundAmount' }) });
                }
              } else {
                handleCancelPayment();
              }
            },
            disabled:
              currentSubmission?.payment?.status == 'canceled' ||
              currentSubmission?.payment?.status == 'refunded' ||
              currentSubmission?.payment?.status == 'pending_refund' ||
              currentSubmission?.payment?.status == 'failed',
          },
        ]}
        modalHeading={intl.formatMessage({ id: 'formList.submission.refund' })}
        show={showModalRefund}
        modalSize="lg"
      >
        <div className="d-flex flex-column align-items-center justify-content-center">
          <div className="d-flex flex-column align-items-center justify-content-center">
            {currentSubmission && paiementInfoTable(currentSubmission)}
            {currentSubmission?.payment?.status == 'succeeded' && (
              <Formik
                initialValues={{
                  amount: totalAmount,
                }}
                validationSchema={refundAmountErrorMessages}
                onSubmit={() => handleRefund()}
                innerRef={refundRef}
              >
                {() => (
                  <div>
                    <Field name={'amount'}>
                      {({ meta, field }) => {
                        return (
                          <div className="d-flex flex-row align-items-end justify-content-center pb-3">
                            <FieldWrapper
                              error={meta.error}
                              touched={meta.touched}
                              hiddenText={false}
                              textColor={undefined}
                              required={false}
                              label={intl.formatMessage({ id: 'formList.submission.refundAmount' })}
                              visible={true}
                              size={12}
                            >
                              <CurrencyInput
                                id="amount"
                                value={field.value}
                                error={meta.error}
                                touched={meta.touched}
                                onChange={(e) => field.onChange(e)}
                                onBlur={(e) => field.onBlur(e)}
                                enabled={partialRefund}
                              ></CurrencyInput>
                            </FieldWrapper>
                          </div>
                        );
                      }}
                    </Field>
                    <div className="d-flex flex-column align-items-center justify-content-center">
                      <CheckboxInput
                        id="partialRefund"
                        value={partialRefund}
                        onChange={(e) => {
                          setPartialRefund(e.target.checked);
                          if (refundStripeFees && e.target.checked) {
                            setRefundStripeFees(false);
                          } else {
                            setRefundStripeFees(true);
                          }
                        }}
                        label={intl.formatMessage({ id: 'formList.submission.chooseRefundAmount' })}
                      ></CheckboxInput>
                    </div>
                    <div className="d-flex flex-column align-items-center justify-content-center">
                      <CheckboxInput
                        id="stripeFees"
                        value={refundStripeFees}
                        onChange={(e) => {
                          setRefundStripeFees(e.target.checked);
                        }}
                        label={intl.formatMessage({ id: 'formList.submission.refundStripeFees' })}
                        style={{
                          color: partialRefund ? 'rgba(0, 51, 145, 0.7)' : 'rgba(0, 51, 145, 1)',
                        }}
                        enabled={!partialRefund && Number(stripeFeesAmount) > 0}
                      ></CheckboxInput>
                    </div>
                  </div>
                )}
              </Formik>
            )}
            {/* {currentSubmission?.payment?.status != 'succeeded' && (
              <span className="cancel-payment">
                {intl.formatMessage({ id: 'formList.submission.confirmCancelPayment' })}
              </span>
            )} */}
          </div>
        </div>
      </ModalComponent>
    </div>
  );
};

export default SubmissionPage;
